import React, { useEffect, useState, useCallback } from "react";
import { LuCirclePlus } from "react-icons/lu";
import { Link, useNavigate } from "react-router-dom";
import ActionButtons from "../components/ActionButtons";
import { ImageLink, getData, updateData, deleteData } from "../services/axios.function";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import Pagination from "../components/Pagination";

const Carol = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const limit = 10;
  const [totalPages, setTotalPages] = useState(1);

  // festival filter
  const [festivalList, setFestivalList] = useState([]);
  const [festivalType, setFestivalType] = useState("all");

  /* =========================
      FETCH FESTIVAL LIST
     ========================= */
  const fetchFestivals = useCallback(async () => {
    try {
      const res = await getData("api/festival/get-all");
      if (res?.data?.isSuccess) {
        setFestivalList(res.data.data || []);
      }
    } catch (err) {
      toast.error("Failed to load festival list");
    }
  }, []);

  const fetchCarolSection = useCallback(async (
    currentPage = 1,
    selectedFestival = festivalType
  ) => {
    try {
      setLoading(true);

      let url = `api/carol/pagination?page=${currentPage}&limit=${limit}`;

      if (selectedFestival !== "all") {
        url += `&type=${selectedFestival}`;
      }

      const response = await getData(url);

      if (response?.data?.isSuccess) {
        setData(response.data.data || []);
        setTotalPages(response.data.totalPages || 1);
      } else {
        // ✅ CLEAR OLD DATA HERE
        setData([]);
        setTotalPages(1);
      }
    } catch (error) {
      setData([]); // safety clear
      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong!"
      );
    } finally {
      setLoading(false);
    }
  }, [festivalType]);

  const handleStatusToggle = async (id, isActive) => {
    try {
      const payload = {
        fieldId: id,
        isActive: !isActive,
        modelName: "Carol",
      };

      const response = await updateData(
        "api/user/update-status",
        payload
      );

      if (response?.data?.isSuccess) {
        setData((prevData) =>
          prevData.map((item) =>
            item._id === id
              ? { ...item, isActive: payload.isActive }
              : item
          )
        );

        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong!"
      );
    }
  };


  const deleteCarolImage = async (id) => {
    const response = await deleteData(
      `api/carol/delete/${id}`
    );
    if (response.status === 200) {
      toast.success(response.data.message);
      fetchCarolSection();
    }
  };

  const handleUpdate = async (data) => {
    navigate(`/add-carol`, {
      state: data,
    });
  };

  useEffect(() => {
    fetchFestivals();
  }, [fetchFestivals]);

  useEffect(() => {
    fetchCarolSection(page, festivalType);
  }, [page, festivalType, fetchCarolSection]);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="container-fluid">
      <div className="name-search-btn">
        <h4>Carols</h4>
        <div className="search-n-btn">
          {/* Festival Filter */}
          <div className="filter-dropdown">
            <label className="filter-label">Festival :</label>
            <select
              className="custom-select"
              value={festivalType}
              onChange={(e) => {
                setFestivalType(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">All Festivals</option>
              {festivalList?.map((festival) => (
                <option key={festival._id} value={festival._id}>
                  {festival.name}
                </option>
              ))}
            </select>
          </div>

          {/* Add Button */}
          <div className="add-btn">
            <Link to="/add-carol">
              <button type="button">
                <LuCirclePlus size={20} />
                <span>Add Carol</span>
              </button>
            </Link>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12 grid-margin stretch-card">
          <div className="card border-0">
            <div className="card-body">
              <div className="table-responsive border rounded p-1">
                <table className="table text-center">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Song</th>
                      <th>Title</th>
                      <th>Festival</th>
                      <th>isActive</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.length > 0 ? (
                      data?.map((item, index) => (
                        <tr key={item._id}>
                          <td>{item?.sortOrder}</td>
                          {/* SONG COLUMN */}
                          <td>
                            {item.song ? (
                              <audio
                                controls
                                preload="none"
                                style={{ width: "250px" }}
                              >
                                <source
                                  src={ImageLink(item.song)}
                                  type="audio/mpeg"
                                />
                                Your browser does not support the audio element.
                              </audio>
                            ) : (
                              <span>No Song</span>
                            )}
                          </td>

                          <td>{item.title}</td>
                          <td>{item?.type?.name}</td>
                          <td>
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                name="isActive"
                                checked={item.isActive}
                                onChange={() => handleStatusToggle(item._id, item.isActive)}
                              />
                              <span className="slider" />
                            </label>
                          </td>
                          <td>
                            <div className="action-btn">
                              <ActionButtons
                                handleDelete={deleteCarolImage}
                                handleEdit={handleUpdate}
                                data={item}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-4">
                          No Data Found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Add pagination here */}
              {totalPages > 1 && (
                <Pagination
                  pages={totalPages}
                  page={page}
                  onPageChange={(p) => setPage(p)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Carol;
